import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getContactSubmissions,
  markContactAsRead,
  deleteContactSubmission,
} from '../../services/firebaseService';
import toast from 'react-hot-toast';

export default function AdminContacts() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  async function fetchMessages() {
    try {
      const data = await getContactSubmissions();
      setMessages(data);
    } catch {
      toast.error(t('admin.failedLoad'));
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkRead(id) {
    try {
      await markContactAsRead(id);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, isRead: true } : m));
      if (selectedMessage?.id === id) {
        setSelectedMessage(prev => ({ ...prev, isRead: true }));
      }
      toast.success(t('admin.markedAsRead'));
    } catch {
      toast.error(t('admin.failedSave'));
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('admin.deleteMessageConfirm'))) return;
    try {
      await deleteContactSubmission(id);
      setMessages(prev => prev.filter(m => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
      toast.success(t('admin.messageDeleted'));
    } catch {
      toast.error(t('admin.failedDelete'));
    }
  }

  function formatDate(timestamp) {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  const unreadCount = messages.filter(m => !m.isRead).length;

  return (
    <div className="admin-contacts">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.contactMessages')}</h1>
          <p>{t('admin.messagesCount', { count: messages.length })} · {t('admin.unreadCount', { count: unreadCount })}</p>
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="admin-empty">
          <i className="fas fa-inbox"></i>
          <p>{t('admin.noMessagesYet')}</p>
        </div>
      ) : (
        <div className="admin-messages-layout">
          {/* Messages list */}
          <div className="admin-messages-list">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`admin-message-item ${!msg.isRead ? 'unread' : ''} ${selectedMessage?.id === msg.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedMessage(msg);
                  if (!msg.isRead) handleMarkRead(msg.id);
                }}
              >
                <div className="admin-message-header">
                  <strong>{msg.name || 'Unknown'}</strong>
                  {!msg.isRead && <span className="admin-unread-dot"></span>}
                </div>
                <div className="admin-message-subject">{msg.subject || t('admin.noSubject')}</div>
                <div className="admin-message-preview">{msg.message?.substring(0, 80)}...</div>
                <div className="admin-message-meta">{formatDate(msg.createdAt)}</div>
              </div>
            ))}
          </div>

          {/* Message detail */}
          <div className="admin-message-detail">
            {selectedMessage ? (
              <div className="admin-card">
                <div className="admin-message-detail-header">
                  <h2>{selectedMessage.subject || t('admin.noSubject')}</h2>
                  <div className="admin-actions">
                    {!selectedMessage.isRead && (
                      <button onClick={() => handleMarkRead(selectedMessage.id)} className="admin-action-btn edit" title={t('admin.markedAsRead')}>
                        <i className="fas fa-check"></i>
                      </button>
                    )}
                    <button onClick={() => handleDelete(selectedMessage.id)} className="admin-action-btn delete" title={t('admin.failedDelete')}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div className="admin-message-info">
                  <p><strong>{t('admin.from')}:</strong> {selectedMessage.name}</p>
                  <p><strong>{t('admin.email')}:</strong> <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a></p>
                  {selectedMessage.phone && <p><strong>{t('admin.phone')}:</strong> {selectedMessage.phone}</p>}
                  <p><strong>{t('admin.date')}:</strong> {formatDate(selectedMessage.createdAt)}</p>
                </div>
                <div className="admin-message-body">
                  {selectedMessage.message}
                </div>
              </div>
            ) : (
              <div className="admin-empty">
                <i className="fas fa-envelope-open-text"></i>
                <p>{t('admin.selectMessage')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
